import {
  WEATHER_SCALE_DARK_COLOR,
  WEATHER_SCALE_LIGHT_COLOR,
} from "@sencrop/ui-components";
import {
  WeatherColors,
  WeatherScale,
} from "@sencrop/ui-components/dist/types/utils/weather-scale/types";
import {
  Map,
  PinMarker,
  PixiLayer,
  PixiWeatherMarker,
  PixiWeatherRegionMarker,
} from "@sencrop/ui-map";
import type { GeoJsonObject } from "geojson";
import * as L from "leaflet";
import { maxBy, minBy } from "lodash";
import { useMemo, useRef, useState } from "react";
import { GeoJSON } from "react-leaflet";
import { useQuery } from "react-query";
import styled from "styled-components";
import {
  getAllActiveDevices,
  getAllInactiveDevices,
} from "../../../../api/devices";
import { useProgressiveRender } from "../../../../hooks/useProgressiveRender";
import { Device } from "../../../../models/device";
import { SearchLocation } from "../../../../models/search-location";
import DeviceInfoCard from "./DeviceInfoCard";
import LocationCard from "../../components/LocationCard";
import MapLoadingOverlay from "../../components/MapLoadingOverlay";
import ZoneCircle from "../../components/ZoneCircle";
import MapTopControls from "../MapTopControls";
import germanyStates from "./germany-states.json";
import devicesMeasures from "./rainfall_data.json";

type DeviceWithMeasure = Device & {
  totalRain: number;
  nbOfActiveDays: number;
};

const PLUVIOMETRY_SCALE_COLORS: WeatherColors[] = [
  ["#ffffff", WEATHER_SCALE_DARK_COLOR],
  ["#f4fbfc", WEATHER_SCALE_DARK_COLOR],
  ["#e9f6f9", WEATHER_SCALE_DARK_COLOR],
  ["#def2f6", WEATHER_SCALE_DARK_COLOR],
  ["#d3edf2", WEATHER_SCALE_DARK_COLOR],
  ["#c8e9ef", WEATHER_SCALE_DARK_COLOR],
  ["#bde4ec", WEATHER_SCALE_DARK_COLOR],
  ["#b2e0e9", WEATHER_SCALE_DARK_COLOR],
  ["#a7dce6", WEATHER_SCALE_DARK_COLOR],
  ["#9cd7e3", WEATHER_SCALE_DARK_COLOR],
  ["#91d3df", WEATHER_SCALE_DARK_COLOR],
  ["#86cedc", WEATHER_SCALE_DARK_COLOR],
  ["#7bcad9", WEATHER_SCALE_DARK_COLOR],
  ["#70c6d6", WEATHER_SCALE_DARK_COLOR],
  ["#65c1d3", WEATHER_SCALE_LIGHT_COLOR],
  ["#5abdd0", WEATHER_SCALE_LIGHT_COLOR],
  ["#4fb8cc", WEATHER_SCALE_LIGHT_COLOR],
  ["#39afc6", WEATHER_SCALE_LIGHT_COLOR],
  ["#39afc6", WEATHER_SCALE_LIGHT_COLOR],
  ["#2eabc3", WEATHER_SCALE_LIGHT_COLOR],
];

const LOCATION_ZOOM = 9;

const DEFAULT_CIRCLE_RADIUS = 40;

const PIXI_LAYER_OPTIONS = {
  padding: 0.1,
  resolution: L.Browser.retina ? 2 : 1,
};

const ZOOM_TRANSFORM = (zoom: number) => {
  return zoom;
};

const RainfallMap = () => {
  const [selectedDevice, setSelectedDevice] = useState<DeviceWithMeasure>();

  const { isLoading: isLoadingActiveDevices, data: activeDevices = [] } =
    useQuery(["activeDevices"], getAllActiveDevices, {
      refetchOnWindowFocus: false,
    });

  const { isLoading: isLoadingInactiveDevices, data: inactiveDevices = [] } =
    useQuery(["inactiveDevices"], getAllInactiveDevices, {
      refetchOnWindowFocus: false,
    });

  const devices = useMemo<DeviceWithMeasure[]>(() => {
    return [...inactiveDevices, ...activeDevices]
      .map((device) => {
        const measure = devicesMeasures.find(
          (m) => m.identification === device.identification
        );
        if (!measure) return;
        return {
          ...device,
          totalRain: measure.total_rain,
          nbOfActiveDays: measure.nb_of_active_days,
        };
      })
      .filter((d) => d !== undefined) as DeviceWithMeasure[];
  }, [inactiveDevices, activeDevices]);

  const isLoading = useMemo(
    () => isLoadingActiveDevices || isLoadingInactiveDevices,
    [isLoadingActiveDevices, isLoadingInactiveDevices]
  );

  const isNotLoading = useMemo(() => !isLoading, [isLoading]);

  const handleMarkerClick = (id: number) => {
    console.log("marker clicked", id);
    const device = devices.find((d) => d.id === id);
    setSelectedDevice((current) => (current === device ? undefined : device));
  };

  const handleCardClose = () => setSelectedDevice(undefined);

  const [location, setLocation] = useState<SearchLocation>();

  const mapRef = useRef<L.Map>();

  const handleLocationChange = (l?: SearchLocation) => {
    setLocation(l);

    if (l) mapRef.current?.setView(l, LOCATION_ZOOM);
  };

  const handleSearchDeviceChange = (value?: string) => {
    const device = displayedDevices.find(
      (d) => String(d.id) === value || d.identification === value
    );

    if (device) {
      setLocation({ lat: device.position.lat, lng: device.position.lng });
      mapRef.current?.setView(device.position, LOCATION_ZOOM);
    }
  };

  const [circleRadius, setCircleRadius] = useState<number>(
    DEFAULT_CIRCLE_RADIUS
  );

  const {
    visibleItems: displayedDevices,
    isRendering,
    renderProgress,
  } = useProgressiveRender<DeviceWithMeasure>(devices);

  const pluviometryScale = useMemo<WeatherScale>(
    () => ({
      colors: PLUVIOMETRY_SCALE_COLORS,
      min: minBy(devices, (d) => d.totalRain)?.totalRain ?? 0,
      max: maxBy(devices, (d) => d.totalRain)?.totalRain ?? 1000,
    }),
    []
  );

  return (
    <Container>
      <Map
        topControls={
          isNotLoading && (
            <MapTopControls
              onSearchLocationChange={handleLocationChange}
              onSearchDeviceChange={handleSearchDeviceChange}
            />
          )
        }
        bottomControls={
          isNotLoading && (
            <>
              {location ? (
                <LocationCard
                  location={location}
                  locationRadius={circleRadius}
                  onLocationRadiusChange={setCircleRadius}
                  devices={devices}
                />
              ) : null}
            </>
          )
        }
        whenCreated={(map) => (mapRef.current = map)}
      >
        <GeoJSON
          data={germanyStates as GeoJsonObject}
          style={{ fill: false }}
        />
        <PixiLayer options={PIXI_LAYER_OPTIONS} zoomTransform={ZOOM_TRANSFORM}>
          {isNotLoading &&
            displayedDevices.map((device) => {
              if (device.identification.startsWith("DWD")) {
                return (
                  <PixiWeatherRegionMarker
                    position={device.position}
                    id={device.id}
                    key={device.id}
                    value={device.totalRain}
                    label={String(Math.floor(device.totalRain))}
                    scale={pluviometryScale}
                    onClick={() => handleMarkerClick(device.id)}
                  />
                );
              }
              return (
                <PixiWeatherMarker
                  position={device.position}
                  id={device.id}
                  key={device.id}
                  value={device.totalRain}
                  label={String(Math.floor(device.totalRain))}
                  scale={pluviometryScale}
                  onClick={() => handleMarkerClick(device.id)}
                />
              );
            })}
        </PixiLayer>
        {location && (
          <>
            {circleRadius ? (
              <ZoneCircle position={location} radius={circleRadius} />
            ) : null}
            <PinMarker
              position={location}
              icon="user"
              variant="position"
              size={50}
            />
          </>
        )}
      </Map>
      {selectedDevice && (
        <DeviceInfoCard device={selectedDevice} onClose={handleCardClose} />
      )}
      {isLoading && <MapLoadingOverlay />}
      {isNotLoading && isRendering && (
        <MapLoadingOverlay
          position={["bottom", "left"]}
          size={30}
          blocking={false}
        >
          {renderProgress} %
        </MapLoadingOverlay>
      )}
    </Container>
  );
};

export default RainfallMap;

const Container = styled.div`
  position: relative;
  grid-area: map;
  display: flex;
  width: 100%;
  height: 100%;
`;
